import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  alpha,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useUI } from '../../context/UIContext';
import { useSession } from '../../context/SessionContext';
import { useConfig } from '../../context/ConfigContext';
import { useResources } from '../../context/ResourceContext';
import ResourceViewerComponent from './ResourceViewerComponent';
import ResourceTypeIcon from '../resources/ResourceTypeIcon';
import ConditionalTooltip from '../common/ConditionalTooltip';
import { getContentTypeConfig } from './ContentTypeRegistry';
import DomainObjectModal from '../domain/DomainObjectModal';

const ContentContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isMinimized' && prop !== 'isDragging',
})(({ theme, isMinimized, isDragging }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  overflow: 'visible',
  position: 'relative',
  zIndex: 5,
  minHeight: 0,
  overscrollBehaviorY: 'contain',
}));

const TabBarContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1, 0, 1),
    minHeight: '48px',
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const ContentBody = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  position: 'relative',
  minHeight: 0,
  padding: theme.spacing(2),
  overscrollBehaviorY: 'contain',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const ResizeHandle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragging',
})(({ theme, isDragging }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '12px',
  cursor: 'ns-resize',
  backgroundColor: isDragging ? alpha(theme.palette.action.selected, 0.5) : 'transparent',
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  position: 'relative',
  transition: 'background-color 0.2s ease',
}));

const CHAT_AREA_MIN_HEIGHT = 0;

const ContentPanel = () => {
  const {
    openResources,
    activeResourceId,
    setActiveResourceTab,
    closeResourceTab,
    closeAllResourceTabs,
    contentPanelHeight,
    setContentPanelHeight,
    isContentMinimized,
    toggleMinimize,
    previewModal,
    openResourcePreview,
    closeResourcePreview,
  } = useUI();
  const { teamName, sessionContext } = useSession();
  const { chatTeamConfigs, domainConfigs } = useConfig();
  const { getResourceById } = useResources();

  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef(null);
  const isResizingRef = useRef(false);
  const dragInfoRef = useRef({
    startY: 0,
    startHeight: 0,
    mouseDownTime: 0,
    isDragging: false
  });

  const { resource: previewResource, options: previewOptions } = previewModal || {};

  const uniqueOpenResources = useMemo(() => {
    const seen = new Set();
    return openResources.filter(resource => {
      const id = String(resource.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [openResources]);

  const isResourceClosablePure = useCallback((resource) => {
    if (!resource || !teamName || !chatTeamConfigs) {
      return true;
    }
    const teamConfig = chatTeamConfigs[teamName];
    if (!teamConfig?.nonClosableResourceTypes?.length) {
      return true;
    }

    const resourceType = resource.type || resource.resource_type;
    const isNonClosableType = teamConfig.nonClosableResourceTypes.includes(resourceType);

    if (!isNonClosableType) {
      return true;
    }

    // Check if this specific resource ID is in the current session context
    const isInContext = (sessionContext || []).some(ctx => String(ctx.id) === String(resource.id));
    if (isInContext) {
      return false;
    }

    // Check if this resource is a dependency of something in the context
    const isDependency = (sessionContext || []).some(ctx => {
      const contextResource = getResourceById(ctx.id);
      if (!contextResource) return false;
      
      const targetId = String(resource.id);
      return (
        String(contextResource.workout_plan_id) === targetId ||
        String(contextResource.location_id) === targetId ||
        String(contextResource.parent_id) === targetId ||
        String(contextResource.chat_session_id) === targetId
      );
    });

    if (isDependency) {
      return false;
    }

    return true;
  }, [teamName, sessionContext, chatTeamConfigs, getResourceById]);

  // Effect to ensure non-closable resources stay open is handled in SessionContext via autoOpenResourceTypes
  // This effect is mainly for debugging or specific edge case handling if needed
  useEffect(() => {
    uniqueOpenResources.forEach(resource => {
      if (!isResourceClosablePure(resource)) {
        // Resource is locked
      }
    });
  }, [uniqueOpenResources, isResourceClosablePure, teamName]);

  const handleCloseTab = useCallback((resourceId) => {
    const resourceToClose = uniqueOpenResources.find(res => String(res.id) === String(resourceId));
    if (isResourceClosablePure(resourceToClose)) {
      closeResourceTab(resourceId);
    } else {
      console.warn(`Attempted to close a non-closable resource tab: ${resourceId}`);
    }
  }, [uniqueOpenResources, isResourceClosablePure, closeResourceTab]);

  const canCloseAll = useMemo(() => 
    uniqueOpenResources.every(res => isResourceClosablePure(res)),
    [uniqueOpenResources, isResourceClosablePure]
  );

  const handleCloseAllTabs = useCallback(() => {
    const closableResourceIds = uniqueOpenResources
      .filter(res => isResourceClosablePure(res))
      .map(res => res.id);
    
    closableResourceIds.forEach(id => closeResourceTab(id));
  }, [uniqueOpenResources, isResourceClosablePure, closeResourceTab]);

  const handleResizeStart = useCallback((e) => {
    // Prevent resize if clicking buttons inside the handle
    if (e.target.closest('button[data-button-type="arrow"]')) return;
    
    e.preventDefault();
    if (!panelRef.current || isContentMinimized) return;

    isResizingRef.current = true;
    const currentHeight = panelRef.current.offsetHeight;
    
    dragInfoRef.current = {
      isDragging: true,
      startY: e.clientY,
      startHeight: currentHeight,
      mouseDownTime: Date.now()
    };

    setIsDragging(true);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    
    // Set explicit pixel height to prevent jumping during resize
    panelRef.current.style.height = `${currentHeight}px`;

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeEnd);
    window.addEventListener('mouseleave', handleResizeEnd);
  }, [isContentMinimized]);

  const handleResizeMove = useCallback((e) => {
    if (!dragInfoRef.current.isDragging || !panelRef.current?.parentElement) return;

    const deltaY = e.clientY - dragInfoRef.current.startY;
    let newHeight = dragInfoRef.current.startHeight + deltaY;

    // Constraints
    const minHeight = 100;
    const containerHeight = panelRef.current.parentElement.offsetHeight;
    const maxHeight = containerHeight - CHAT_AREA_MIN_HEIGHT;

    newHeight = Math.max(minHeight, Math.min(newHeight, maxHeight));
    
    panelRef.current.style.height = `${newHeight}px`;
  }, []);

  const handleResizeEnd = useCallback((e) => {
    const timeSinceDown = Date.now() - dragInfoRef.current.mouseDownTime;
    const distanceMoved = Math.abs(e.clientY - dragInfoRef.current.startY);

    if (dragInfoRef.current.isDragging) {
      // If it was a click (short time, little movement), reset to default
      if (timeSinceDown < 300 && distanceMoved < 5) {
         setContentPanelHeight('50%');
      } else if (panelRef.current) {
         // Save the final pixel height
         setContentPanelHeight(`${panelRef.current.offsetHeight}px`);
      }
    }

    dragInfoRef.current.isDragging = false;
    isResizingRef.current = false;
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    window.removeEventListener('mousemove', handleResizeMove);
    window.removeEventListener('mouseup', handleResizeEnd);
    window.removeEventListener('mouseleave', handleResizeEnd);
  }, [setContentPanelHeight, handleResizeMove]);

  const handleMaximizeContentPanel = useCallback(() => {
    if (!panelRef.current?.parentElement) return;
    const containerHeight = panelRef.current.parentElement.offsetHeight;
    const newHeight = containerHeight - CHAT_AREA_MIN_HEIGHT;
    setContentPanelHeight(`${newHeight}px`);
  }, [setContentPanelHeight]);

  const handleButtonMouseDown = (e) => e.stopPropagation();

  const handleCloseClick = (e, resourceId) => {
    e.stopPropagation();
    handleCloseTab(resourceId);
  };

  const handleMinimizedResourceClick = (resource) => {
    openResourcePreview(resource, { preferDomainModal: false });
  };

  const handleClosePreview = () => {
    closeResourcePreview();
  };

  const activeResourceData = useMemo(() => {
    if (!activeResourceId) return null;
    return uniqueOpenResources.find(res => String(res.id) === String(activeResourceId));
  }, [uniqueOpenResources, activeResourceId]);

  const previewDialog = useMemo(() => {
    if (!previewResource) return null;

    const resourceType = previewResource.type || previewResource.resource_type;
    const domainConfig = Object.values(domainConfigs || {}).find(
      cfg => cfg.resourceType === resourceType
    );

    if (domainConfig) {
      return (
        <DomainObjectModal
          open={!!previewResource}
          onClose={handleClosePreview}
          itemToEdit={previewResource}
          config={domainConfig}
          sectionTitle={domainConfig.singularName}
          initialMode="view"
          onActionSuccess={(action, item) => {
             if (action === 'delete') handleClosePreview();
          }}
        />
      );
    }

    // Fallback generic viewer
    return (
      <Dialog
        open={!!previewResource}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="lg"
        aria-labelledby="preview-resource-dialog-title"
        sx={{
          '& .MuiDialog-paper': {
            '@media (max-width: 600px)': {
              margin: 0,
              maxWidth: '100%',
              width: '100%',
              borderRadius: 0,
              height: '100%',
              maxHeight: '100%',
            }
          }
        }}
      >
        <DialogTitle
          id="preview-resource-dialog-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '@media (max-width: 600px)': { px: 1, py: 1 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ResourceTypeIcon type={previewResource.type || previewResource.resource_type} />
            {getContentTypeConfig(previewResource.type || previewResource.resource_type).getTitle(previewResource)}
          </Box>
          <IconButton aria-label="close" onClick={handleClosePreview}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 0,
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            '@media (max-width: 600px)': { height: 'calc(100vh - 120px)', p: 0 }
          }}
        >
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, '@media (max-width: 600px)': { p: 0 } }}>
            <ResourceViewerComponent content={previewResource} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ '@media (max-width: 600px)': { px: 1, py: 1 } }}>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }, [previewResource, previewOptions, domainConfigs, handleClosePreview]);

  if (uniqueOpenResources.length === 0) {
    return previewDialog;
  }

  return (
    <ContentContainer
      id="content-panel-container"
      elevation={2}
      isMinimized={isContentMinimized}
      isDragging={isDragging}
      ref={panelRef}
      style={{
        height: isContentMinimized ? 'auto' : contentPanelHeight,
      }}
      sx={{
        maxHeight: isContentMinimized ? 'none' : '85%',
        transition: isDragging ? 'none' : 'height 0.2s ease',
      }}
    >
      <TabBarContainer>
        <Box sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          flexGrow: 1,
          mr: 1,
          alignItems: 'center',
          py: 0.5,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}>
          {uniqueOpenResources.map((resource) => {
            const { getTitle } = getContentTypeConfig(resource.type || resource.resource_type);
            const resourceTitle = getTitle(resource);
            const isActive = !isContentMinimized && String(resource.id) === String(activeResourceId);
            const closable = isResourceClosablePure(resource);

            return (
              <Button
                key={resource.id}
                variant={isActive ? "contained" : "outlined"}
                size="small"
                onClick={() => isContentMinimized ? handleMinimizedResourceClick(resource) : setActiveResourceTab(resource.id)}
                startIcon={
                  <ResourceTypeIcon
                    type={resource.type || resource.resource_type}
                    sx={isActive ? { color: 'inherit' } : undefined}
                  />
                }
                endIcon={
                  !isContentMinimized ? (
                    closable ? (
                      <Box
                        component="span"
                        onClick={(e) => handleCloseClick(e, resource.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          p: 0.25,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: '1rem' }} />
                      </Box>
                    ) : null
                  ) : (
                    <OpenInNewIcon sx={{ fontSize: '0.8rem !important' }} />
                  )
                }
                sx={{
                  borderRadius: 1,
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  textTransform: 'none',
                  borderColor: (theme) => isActive ? 'primary.main' : theme.palette.divider,
                  color: isActive ? 'primary.contrastText' : 'text.primary',
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: (theme) => isActive ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                  {resourceTitle}
                </Typography>
              </Button>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <ConditionalTooltip title={isContentMinimized ? "Expand Panel" : "Minimize Panel"}>
            <IconButton
                size="small"
                onClick={toggleMinimize}
                aria-label={isContentMinimized ? "expand panel" : "minimize panel"}
            >
              {isContentMinimized ?  <KeyboardArrowDownIcon/> : <KeyboardArrowUpIcon />}
            </IconButton>
          </ConditionalTooltip>
          {canCloseAll && (
            <ConditionalTooltip title="Close All Tabs">
              <span>
                <IconButton
                  size="small"
                  onClick={handleCloseAllTabs}
                  aria-label="close all tabs"
                  disabled={uniqueOpenResources.length === 0}
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </ConditionalTooltip>
          )}
        </Box>
      </TabBarContainer>

      {!isContentMinimized && (
        <ContentBody>
          {activeResourceData ? (
            <ResourceViewerComponent key={activeResourceId} content={activeResourceData} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>
                {!activeResourceId && "Select a resource tab to view its content."}
                {activeResourceId && !activeResourceData && `Error: Could not find data for resource ID ${activeResourceId}. It might have been closed or removed.`}
              </Typography>
            </Box>
          )}
        </ContentBody>
      )}

      {!isContentMinimized && (
        <ResizeHandle
          isDragging={isDragging}
          onMouseDown={handleResizeStart}
          aria-label="resize panel"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              height: 40,
            }}
          >
            <ConditionalTooltip title="Maximize resource view">
              <IconButton
                onClick={handleMaximizeContentPanel}
                onMouseDown={handleButtonMouseDown}
                data-button-type="arrow"
                sx={{
                  p: 1,
                  backgroundColor: 'background.paper',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  boxShadow: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.1)',
                  },
                  transition: 'transform 0.15s ease-in-out',
                }}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </ConditionalTooltip>

            <ConditionalTooltip title="Click to center, or drag to resize">
              <IconButton
                sx={{
                  p: 1,
                  backgroundColor: 'background.paper',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  boxShadow: 1,
                  cursor: 'ns-resize',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.1)',
                  },
                  transition: 'transform 0.15s ease-in-out',
                }}
              >
                <DragHandleIcon fontSize="small" />
              </IconButton>
            </ConditionalTooltip>

            <ConditionalTooltip title="Minimize Panel">
              <IconButton
                onClick={toggleMinimize}
                onMouseDown={handleButtonMouseDown}
                data-button-type="arrow"
                sx={{
                  p: 1,
                  backgroundColor: 'background.paper',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  boxShadow: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'scale(1.1)',
                  },
                  transition: 'transform 0.15s ease-in-out',
                }}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </ConditionalTooltip>
          </Box>
        </ResizeHandle>
      )}
      {previewDialog}
    </ContentContainer>
  );
};

export default ContentPanel;